/**
 * PricingOrbit — тарифы в виде орбитального слайдера.
 *
 * Механика взята из референса владельца «Interactive Gaming Slider»
 * (огромное колесо, наполовину спрятанное за левым краем; элементы
 * орбитят как на чёртовом колесе, активный «выезжает» из дуги и
 * снимает blur; справа въезжает инфо-панель; фон меняет оттенок):
 *   · колесо = div с rotate(wheelDeg), медальоны расставлены через
 *     rotate(-360/N*i) c transform-origin в центре круга
 *   · контр-поворот контента медальона держит его вертикально
 *   · next/prev крутят колесо на ±120° (3 тарифа)
 *   · активный медальон: scale-up + выдвижение из дуги + снятие blur
 *   · инфо-панель справа: translateX+opacity свайп под активный тариф
 *   · свечение секции меняет оттенок под тариф (вместо смены фона body)
 *
 * Мобайл (<lg): колесо скрыто — медальон по центру + инфо-панель ниже.
 */
import { useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import { useLang } from "../i18n";
import { TIER_ORDER, POPULAR_TIER } from "../content/pricing";
import { usePublicPlans, resolveTiers, type ResolvedTier } from "../lib/pricingTiers";
import { useReveal } from "../lib/motion";
import { SectionHead } from "./SectionHead";

/** «$25» → 25, чтобы посчитать процент скидки от старой цены. */
const priceToNum = (s: string) => parseInt(s.replace(/[^0-9]/g, ""), 10) || 0;
const discountPct = (oldP: string, newP: string) => {
  const o = priceToNum(oldP);
  const n = priceToNum(newP);
  return o > 0 && n < o ? Math.round((1 - n / o) * 100) : 0;
};

export function PricingOrbit() {
  const { t, lang } = useLang();
  const plans = usePublicPlans();
  const ref = useReveal<HTMLElement>();

  // Тарифы: из БД (super-admin → Планы) с маркетинг-копирайтом из i18n; пока
  // грузятся / при ошибке RPC — статика (фолбэк внутри resolveTiers, сайт не падает).
  const tiers = useMemo(
    () => resolveTiers(t.pricing as unknown as Record<string, unknown>, lang, plans),
    [t, lang, plans],
  );

  // Геометрия колеса зависит от числа тарифов (4 из БД или 5 в фолбэке-статике).
  // SLOTS = N×2: каждый тариф стоит на колесе дважды, соседи всегда выглядывают
  // из дуги. Базовый доворот -180° выводит активный слот на видимую 3-часовую дугу.
  const N = tiers.length;
  const SLOTS = N * 2;
  const STEP = SLOTS > 0 ? 360 / SLOTS : 0;
  const wheelDegFor = (s: number) => STEP * s - 180;

  // slot накапливается без mod — колесо крутится непрерывно в одну сторону
  const [slot, setSlot] = useState(() => Math.max(0, TIER_ORDER.indexOf(POPULAR_TIER))); // Pro по умолчанию
  const wheelDeg = wheelDegFor(slot);
  const active = N > 0 ? ((slot % N) + N) % N : 0;
  const activeSlot = SLOTS > 0 ? ((slot % SLOTS) + SLOTS) % SLOTS : 0;

  const go = (dir: 1 | -1) => setSlot((s) => s + dir);

  const goTo = (tierIndex: number) => {
    // Кратчайший доворот к ближайшему слоту нужного тарифа
    const delta = ((tierIndex - active) % N + N) % N;
    setSlot((s) => s + (delta > N / 2 ? delta - N : delta));
  };

  // --- Drag/swipe рукой или мышью (запрос владельца) ---
  // Горизонталь: влево = следующий; вертикаль (на колесе): вниз = верхняя
  // монета спускается в центр. Каждые DRAG_STEP px — один шаг, живой отклик.
  const DRAG_STEP = 90;
  const dragRef = useRef<{ x: number; y: number; acc: number; total: number; on: boolean }>({
    x: 0, y: 0, acc: 0, total: 0, on: false,
  });
  /** Суммарный сдвиг последнего жеста — глушит click по монете после drag */
  const lastDragTotal = useRef(0);

  const onPointerDown = (e: React.PointerEvent) => {
    dragRef.current = { x: e.clientX, y: e.clientY, acc: 0, total: 0, on: true };
    lastDragTotal.current = 0;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d.on) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    d.x = e.clientX;
    d.y = e.clientY;
    // Доминирующая ось: горизонталь (влево = next) или вертикаль (вниз = next)
    const delta = Math.abs(dx) >= Math.abs(dy) ? -dx : dy;
    d.acc += delta;
    d.total += Math.abs(dx) + Math.abs(dy);
    lastDragTotal.current = d.total;
    while (d.acc >= DRAG_STEP) {
      go(1);
      d.acc -= DRAG_STEP;
    }
    while (d.acc <= -DRAG_STEP) {
      go(-1);
      d.acc += DRAG_STEP;
    }
  };
  const onPointerUp = () => {
    const d = dragRef.current;
    if (!d.on) return;
    d.on = false;
    // Недотянутый жест-свайп (40..90px) тоже листает
    if (Math.abs(d.acc) > 40) go(d.acc > 0 ? 1 : -1);
  };

  const dragHandlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    // pan-y: браузер забирает вертикальный скролл и шлёт pointercancel —
    // без бонус-шага «недотянутого свайпа», иначе скролл листал бы тарифы
    onPointerCancel: () => {
      dragRef.current.on = false;
    },
  };

  if (N === 0) return null; // защитный кейс — tiers всегда ≥4 (статика-фолбэк)
  const activeTier = tiers[active] ?? tiers[0];

  return (
    <section
      id="pricing"
      ref={ref}
      className="relative overflow-hidden border-t"
      style={{ borderColor: "var(--line)" }}
    >
      {/* Свечение, меняющее оттенок под активный тариф */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background: `radial-gradient(48% 60% at 18% 55%, ${activeTier.glow}, transparent 70%)`,
          transition: "background 900ms ease",
        }}
      />

      <div
        className="relative mx-auto w-full max-w-7xl px-5 sm:px-8"
        style={{ paddingTop: "var(--space-section)", paddingBottom: "var(--space-section)" }}
      >
        {/* z-10: заголовок поверх призрачных медальонов верхней дуги колеса */}
        <div className="relative z-10">
          <SectionHead
            num="06"
            label={t.pricing.label}
            title={t.pricing.title}
            accent={t.pricing.accent}
            aside={t.pricing.subtitle}
          />
        </div>

        <div className="relative lg:grid lg:grid-cols-[1fr_1fr] lg:items-center lg:min-h-[540px]">
          {/* ---- Орбитальное колесо (desktop): крутится drag'ом ---- */}
          <div
            className="orbit-stage absolute inset-y-0 left-0 z-0 hidden w-[55%] select-none lg:block"
            aria-hidden
            style={{ touchAction: "pan-y", cursor: "grab" }}
            {...dragHandlers}
          >
            {/* Дуга-обод колеса */}
            <div className="orbit-rim" />
            <div className="orbit-wheel" style={{ transform: `translateY(-50%) rotate(${wheelDeg}deg)` }}>
              {Array.from({ length: SLOTS }, (_, s) => {
                const tier = tiers[s % N];
                const isActive = s === activeSlot;
                return (
                  <div
                    key={s}
                    className="orbit-item"
                    style={{ transform: `rotate(${-STEP * s}deg)` } as React.CSSProperties}
                  >
                    <div className={`orbit-pop${isActive ? " is-active" : ""}`}>
                      {/* Контр-поворот: держим медальон вертикально */}
                      <div style={{ transform: `rotate(${STEP * s - wheelDeg}deg)`, transition: "transform 1s ease" }}>
                        <Medallion
                          tier={tier}
                          active={isActive}
                          perMonth={t.pricing.perMonth}
                          onSelect={
                            isActive
                              ? undefined
                              : () => {
                                  // После drag'а click по монете не считается
                                  if (lastDragTotal.current > 8) return;
                                  goTo(s % N);
                                }
                          }
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Мобайл: медальон-монету убрали — она дублировала цену из инфо-карточки
              ниже и занимала целый экран впустую (фидбек владельца 2026-06-28).
              Переключение тарифов на мобиле — стрелками и точками под карточкой.
              Колесо-орбита остаётся только на десктопе (lg). */}

          {/* ---- Инфо-панель (как .info-item в референсе) ---- */}
          <div className="relative z-10 lg:col-start-2" style={{ minHeight: 420 }}>
            {tiers.map((tier, i) => {
              const isActive = i === active;
              const isPopular = tier.isPopular;
              const isCustom = tier.isCustom;
              return (
                <div
                  key={tier.id}
                  className="inset-0 lg:absolute"
                  style={{
                    display: isActive ? "block" : "none",
                  }}
                >
                  <div
                    key={`${tier.id}-${isActive}`}
                    className="orbit-info"
                  >
                    <div className="mb-5 flex items-center gap-3">
                      <span className="t-eyebrow">{tier.name}</span>
                      {isPopular ? (
                        <span
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.16em]"
                          style={{ background: "var(--gold)", color: "#1a1308" }}
                        >
                          <Sparkles className="h-3 w-3" aria-hidden />
                          {t.pricing.popular}
                        </span>
                      ) : null}
                    </div>

                    <div className="mb-7">
                      <div className="flex items-baseline gap-2">
                        <span
                          className="t-brand text-4xl sm:text-5xl"
                          style={{ color: isPopular ? "var(--gold)" : "var(--fg)" }}
                        >
                          {tier.price}
                          {tier.currency}
                        </span>
                        {!isCustom ? (
                          <span className="text-sm font-medium" style={{ color: "var(--fg-muted)" }}>
                            {t.pricing.perMonth}
                          </span>
                        ) : null}
                      </div>
                      {tier.oldPrice ? (
                        <div className="mt-2.5 flex flex-wrap items-center gap-2.5">
                          <span className="text-base line-through" style={{ color: "var(--fg-muted)" }}>
                            {tier.oldPrice}
                            {tier.currency}
                          </span>
                          <span
                            className="rounded-full px-2.5 py-0.5 text-[11px] font-extrabold"
                            style={{
                              background: "rgba(212,166,86,0.16)",
                              color: "var(--gold-bright)",
                              border: "1px solid var(--gold-line)",
                            }}
                          >
                            −{discountPct(tier.oldPrice, tier.price)}%
                          </span>
                          <span
                            className="text-[10px] font-bold uppercase tracking-[0.18em]"
                            style={{ color: "var(--fg-muted)" }}
                          >
                            {t.pricing.promo}
                          </span>
                        </div>
                      ) : null}
                    </div>

                    <ul className="mb-9 space-y-3">
                      {tier.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 text-sm" style={{ color: "var(--fg-soft)" }}>
                          <Check className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: "var(--gold)" }} aria-hidden />
                          {f}
                        </li>
                      ))}
                    </ul>

                    <a href="#lead" className={isPopular ? "btn-gold" : "btn-ghost"}>
                      {tier.cta}
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ---- Навигация колеса: на desktop — под инфо-панелью (правая колонка) ---- */}
        <div className="relative z-10 mt-10 flex items-center justify-center gap-4 lg:grid lg:grid-cols-[1fr_1fr] lg:justify-stretch">
          <div className="contents lg:col-start-2 lg:flex lg:items-center lg:gap-4">
          <button
            type="button"
            onClick={() => go(-1)}
            className="flex h-12 w-12 items-center justify-center rounded-full border transition-colors hover:border-[var(--gold-line)]"
            style={{ borderColor: "var(--line-strong)", color: "var(--fg)" }}
            aria-label="Предыдущий тариф"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </button>
          <div className="flex items-center gap-2.5" role="tablist" aria-label={t.pricing.label}>
            {tiers.map((tier, i) => (
              <button
                key={tier.id}
                type="button"
                role="tab"
                aria-selected={i === active}
                aria-label={tier.name}
                onClick={() => goTo(i)}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === active ? 26 : 8,
                  height: 8,
                  background: i === active ? "var(--gold)" : "rgba(244,239,230,0.25)",
                }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => go(1)}
            className="flex h-12 w-12 items-center justify-center rounded-full border transition-colors hover:border-[var(--gold-line)]"
            style={{ borderColor: "var(--line-strong)", color: "var(--fg)" }}
            aria-label="Следующий тариф"
          >
            <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
          </div>
        </div>

        {/* Сноски: бесплатный период + срок действия скидок + платное обучение */}
        <div className="relative z-10 mx-auto mt-12 max-w-2xl text-center">
          <div className="mb-4">
            <span
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-extrabold"
              style={{ background: "var(--gold)", color: "#1a1308" }}
            >
              <Sparkles className="h-4 w-4" aria-hidden />
              {t.pricing.freeTrial}
            </span>
          </div>
          <span
            className="inline-flex items-center gap-2.5 rounded-full border px-4 py-2 text-sm font-semibold"
            style={{
              borderColor: "var(--gold-line)",
              color: "var(--gold-bright)",
              background: "rgba(212,166,86,0.06)",
            }}
          >
            <span className="gold-dot" aria-hidden />
            {t.pricing.validUntil}
          </span>
        </div>
      </div>
    </section>
  );
}

/** Медальон-монета тарифа: Starter тёмный, Pro золотой, Enterprise с ободом. */
function Medallion({
  tier,
  active,
  perMonth,
  onSelect,
}: {
  tier: ResolvedTier;
  active: boolean;
  perMonth: string;
  /** Клик по соседней монете — доворот колеса к ней */
  onSelect?: () => void;
}) {
  const isPro = tier.isPopular;
  const isEnterprise = tier.id === "enterprise";
  const isCustom = tier.isCustom;

  return (
    <div
      className={`orbit-medallion${active ? " is-active" : ""}`}
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      aria-label={onSelect ? tier.name : undefined}
      onClick={onSelect}
      onKeyDown={onSelect ? (e) => { if (e.key === "Enter" || e.key === " ") onSelect(); } : undefined}
      style={{
        cursor: onSelect ? "pointer" : "default",
        background: isPro
          ? "radial-gradient(120% 120% at 30% 25%, #ecc987 0%, #d4a656 45%, #8a6a2e 100%)"
          : "radial-gradient(120% 120% at 30% 25%, #211d14 0%, #14110a 60%, #0a0908 100%)",
        border: isPro
          ? "1px solid rgba(255,235,190,0.65)"
          : isEnterprise || isCustom
            ? "2px solid var(--gold-line)"
            : "1px solid var(--line-strong)",
      }}
    >
      {/* Гравировка-обод монеты */}
      <div
        className="absolute inset-3 rounded-full"
        aria-hidden
        style={{
          border: isPro ? "1px dashed rgba(26,19,8,0.35)" : "1px dashed rgba(244,239,230,0.14)",
        }}
      />
      <span
        className="text-[11px] font-extrabold uppercase tracking-[0.3em]"
        style={{ color: isPro ? "rgba(26,19,8,0.75)" : "var(--fg-muted)" }}
      >
        {tier.name}
      </span>
      {tier.oldPrice ? (
        <span
          className="mt-2 text-sm font-semibold line-through"
          style={{ color: isPro ? "rgba(26,19,8,0.55)" : "var(--fg-muted)" }}
        >
          {tier.oldPrice}
          {tier.currency}
        </span>
      ) : null}
      <span
        className="t-brand mt-1 px-4 text-center leading-none"
        style={{
          color: isPro ? "#1a1308" : "var(--fg)",
          fontSize: isCustom ? "1.15rem" : "2.2rem",
        }}
      >
        {tier.price}
        {tier.currency}
      </span>
      {!isCustom ? (
        <span
          className="mt-1 text-sm font-bold"
          style={{ color: isPro ? "rgba(26,19,8,0.7)" : "var(--fg-soft)" }}
        >
          {perMonth}
        </span>
      ) : null}
    </div>
  );
}
